import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Button, Form } from "react-bootstrap";
import Select from "react-select";

function AddGSTMaster() {
  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const routeLocation = useLocation();
  const navigate = useNavigate();

  const [editId, setEditId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Dropdown data
  const [codeTypes, setCodeTypes] = useState([]);
  const [scopes, setScopes] = useState([]);

  const [form, setForm] = useState({
    gst_id: "",
    code_type: "",
    code: "",
    description: "",
    scope: "",
    itc: "",
    rcm: "",
    tds: "",
    is_active: "",
  });

  // 🔹 Helper: normalize values to "Yes"/"No" for Select
  const mapToYesNo = (val) => {
    if (val === "Y" || val === "Yes" || val === true) return "Yes";
    if (val === "N" || val === "No" || val === false) return "No";
    return "";
  };

  // Prefill in edit mode
  useEffect(() => {
    if (routeLocation.state?.GstData) {
      const r = routeLocation.state.GstData;

      setForm({
        gst_id: r.gst_master_id || "",
        code_type: r.code_type_id || "",
        code: r.hsn_sac_code || "",
        description: r.description || "",
        scope: r.scope_id || "",
        // 🔹 Use normalized Yes/No values here
        itc: mapToYesNo(r.input_tax_credit),
        rcm: mapToYesNo(r.reverse_charge_mechanism),
        tds: mapToYesNo(r.tds_applicable),
        is_active: mapToYesNo(r.is_active),
      });

      setEditId(r.gst_master_id);
    }
  }, [routeLocation]);

  // Fetch dropdown lists
  const fetchCodeTypes = async () => {
    try {
      const resp = await axios.post(baseUrl + "/GetCodeType/", {
        action: "READ",
        school_id: userObj.school_id,
      });
      setCodeTypes(resp.data || []);
    } catch (e) {
      console.error("Error fetching code types:", e);
      toast.error("Failed to load code types");
    }
  };

  const fetchScopes = async () => {
    try {
      const resp = await axios.get(baseUrl + "/gst-scope/", {
        action: "READ",
        school_id: userObj.school_id,
      });
      setScopes(resp.data || []);
    } catch (e) {
      console.error("Error fetching scopes:", e);
      toast.error("Failed to load scopes");
    }
  };

  useEffect(() => {
    fetchCodeTypes();
    fetchScopes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Map to react-select options
  const codeTypeOptions = useMemo(
    () =>
      (codeTypes || []).map((x) => ({
        value: x.code_type_id, // store ID
        label: x.code_type_name, // display text
      })),
    [codeTypes]
  );

  const scopeOptions = useMemo(
    () =>
      (scopes || []).map((x) => ({
        value: x.scope_id, // store ID
        label: x.scope_name, // display name
      })),
    [scopes]
  );

  const checkDuplicateHSNSAC = async () => {
    const payload = {
      hsn_sac_code: form.code,
      code_type_id: form.code_type,
      group_master_id: editId || 0, // 0 for ADD, id for EDIT
    };

    const resp = await axios.post(baseUrl + "/gstduplicate/", payload, {
      headers: { "Content-Type": "application/json" },
    });

    return resp.data; // { is_duplicate, duplicate_flag, message }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.code_type || !form.code || !form.scope) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setIsSaving(true);

      // 🔹 STEP 1: DUPLICATE CHECK
      const dupResp = await checkDuplicateHSNSAC();

      if (dupResp?.is_duplicate) {
        toast.error("Record Already Exists with same Code type and Code", {
          position: "top-right",
        });
        return; // ⛔ STOP SUBMISSION
      }

      // 🔹 STEP 2: SAVE DATA
      const payload = {
        hsn_sac_master_id: editId || 0,
        hsn_sac_code: form.code,
        code_type_id: form.code_type,
        description: form.description?.trim(),
        scope_id: form.scope,
        input_tax_credit: form.itc,
        reverse_charge_mechanism: form.rcm,
        tds_applicable: form.tds,
        is_active: form.is_active === "Yes",
        created_by: userObj.username || userObj.user_name,
        action: editId ? "UPDATE" : "CREATE",
      };

      await axios.post(baseUrl + "/addgstmaster/", payload, {
        headers: { "Content-Type": "application/json" },
      });

      toast.success(
        editId ? "Record Updated Successfully" : "Record Added Successfully",
        { position: "top-right" }
      );

      // 🔹 RESET FORM
      setEditId(null);
      setForm({
        gst_id: "",
        code_type: "",
        code: "",
        description: "",
        scope: "",
        itc: "",
        rcm: "",
        tds: "",
        is_active: "",
      });
    } catch (error) {
      console.error("Submit error:", error);

      if (error.response) {
        toast.error(error.response.data?.error || "Server error", {
          position: "top-right",
        });
      } else {
        toast.error("Network error: " + error.message, {
          position: "top-right",
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="pageBody">
      <ToastContainer />
      <Container fluid>
        <div>
          <Card>
            <Card.Body>
              <form onSubmit={handleSubmit}>
                <div>
                  <Row>
                    <Col xs={12}>
                      <h6 className="commonSectionTitle">Add GST Master</h6>
                    </Col>
                  </Row>

                  <Row>
                    {/* Code Type (searchable) */}
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>
                            Code Type <span className="requiredStar">*</span>
                          </Form.Label>
                          <Select
                            inputId="code_type"
                            classNamePrefix="react-select"
                            placeholder="Select Code Type"
                            isSearchable
                            isClearable
                            value={
                              form.code_type
                                ? codeTypeOptions.find(
                                    (o) => o.value === form.code_type
                                  )
                                : null
                            }
                            onChange={(opt) =>
                              setForm((f) => ({
                                ...f,
                                code_type: opt ? opt.value : "",
                              }))
                            }
                            options={codeTypeOptions}
                          />
                        </Form.Group>
                      </div>
                    </Col>

                    {/* Code */}
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>
                            Code <span className="requiredStar">*</span>
                          </Form.Label>
                          <Form.Control
                            required
                            type="text"
                            id="code"
                            value={form.code}
                            placeholder="Enter Code"
                            maxLength={20}
                            onChange={(e) => {
                              const v = e.target.value.replace(
                                /[^A-Za-z0-9\-_.]/g,
                                ""
                              );
                              setForm({ ...form, code: v });
                            }}
                          />
                        </Form.Group>
                      </div>
                    </Col>

                    {/* Description */}
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Description</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={1}
                            id="description"
                            value={form.description}
                            placeholder="Enter Description"
                            maxLength={120}
                            onChange={(e) =>
                              setForm({ ...form, description: e.target.value })
                            }
                          />
                        </Form.Group>
                      </div>
                    </Col>

                    {/* Scope (searchable) */}
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>
                            Scope <span className="requiredStar">*</span>
                          </Form.Label>
                          <Select
                            inputId="scope"
                            classNamePrefix="react-select"
                            placeholder="Select Scope"
                            isSearchable
                            isClearable
                            value={
                              form.scope
                                ? scopeOptions.find(
                                    (o) => o.value === form.scope
                                  )
                                : null
                            }
                            onChange={(opt) =>
                              setForm((f) => ({
                                ...f,
                                scope: opt ? opt.value : "",
                              }))
                            }
                            options={scopeOptions}
                          />
                        </Form.Group>
                      </div>
                    </Col>

                    {/* ITC */}
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>ITC</Form.Label>

                          <Select
                            placeholder="Select ITC"
                            isSearchable
                            options={[
                              { value: "Yes", label: "Yes" },
                              { value: "No", label: "No" },
                            ]}
                            value={
                              form.itc
                                ? {
                                    value: form.itc,
                                    label:
                                      form.itc === "Yes" ? "Yes" : "No",
                                  }
                                : null
                            }
                            onChange={(selected) =>
                              setForm({
                                ...form,
                                itc: selected?.value || "",
                              })
                            }
                            classNamePrefix="react-select"
                          />
                        </Form.Group>
                      </div>
                    </Col>

                    {/* RCM */}
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>RCM</Form.Label>

                          <Select
                            placeholder="Select RCM"
                            isSearchable
                            options={[
                              { value: "Yes", label: "Yes" },
                              { value: "No", label: "No" },
                            ]}
                            value={
                              form.rcm
                                ? {
                                    value: form.rcm,
                                    label:
                                      form.rcm === "Yes" ? "Yes" : "No",
                                  }
                                : null
                            }
                            onChange={(selected) =>
                              setForm({
                                ...form,
                                rcm: selected?.value || "",
                              })
                            }
                            classNamePrefix="react-select"
                          />
                        </Form.Group>
                      </div>
                    </Col>

                    {/* TDS */}
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>TDS</Form.Label>

                          <Select
                            placeholder="Select TDS"
                            isSearchable
                            options={[
                              { value: "Yes", label: "Yes" },
                              { value: "No", label: "No" },
                            ]}
                            value={
                              form.tds
                                ? {
                                    value: form.tds,
                                    label:
                                      form.tds === "Yes" ? "Yes" : "No",
                                  }
                                : null
                            }
                            onChange={(selected) =>
                              setForm({
                                ...form,
                                tds: selected?.value || "",
                              })
                            }
                            classNamePrefix="react-select"
                          />
                        </Form.Group>
                      </div>
                    </Col>

                    {/* IsActive */}
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Status</Form.Label>

                          <Select
                            placeholder="Select Active"
                            isSearchable
                            options={[
                              { value: "Yes", label: "Yes" },
                              { value: "No", label: "No" },
                            ]}
                            value={
                              form.is_active
                                ? {
                                    value: form.is_active,
                                    label:
                                      form.is_active === "Yes"
                                        ? "Yes"
                                        : "No",
                                  }
                                : null
                            }
                            onChange={(selected) =>
                              setForm({
                                ...form,
                                is_active: selected?.value || "",
                              })
                            }
                            classNamePrefix="react-select"
                          />
                        </Form.Group>
                      </div>
                    </Col>
                  </Row>
                </div>

                <div className="d-flex justify-content-between mt-3">
                  <div>
                    <Button
                      type="button"
                      variant="primary"
                      className="btn-info clearBtn"
                      onClick={() =>
                        setForm({
                          gst_id: "",
                          code_type: "",
                          code: "",
                          description: "",
                          scope: "",
                          itc: "",
                          rcm: "",
                          tds: "",
                          is_active: "",
                        })
                      }
                    >
                      Reset
                    </Button>
                  </div>
                  <div>
                    <Button
                      type="button"
                      variant="primary"
                      className="btn-danger secondaryBtn me-2"
                      onClick={() => window.history.back()}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      className="btn-success primaryBtn"
                    >
                      Submit
                    </Button>
                  </div>
                </div>
              </form>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </div>
  );
}

export default AddGSTMaster;
